/**
 * User-send policy + audit (Plan F, F4) — tenant-admin only.
 *
 * The ONE screen where the tenant admin turns the portal's
 * "Send by email" beta on, and decides its guardrails: allowed
 * recipient domains, per-send recipient cap, per-user daily quota, and
 * which email credential every user send authenticates with.
 *
 * The backend refuses enabling without at least one domain AND a
 * credential (422 naming the missing piece) — the form mirrors that so
 * the refusal is visible before saving. Below the form, the append-only
 * audit: this feature moves customer data OUT of the platform, so who
 * sent what to whom is a first-class view, not a SQL query.
 */

import { Badge, Button, Card, CardBody, Select } from "@omnitok/ui";
import { Spinner } from "@pervasivemind/bi-components";
import { AlertCircle, Check, ShieldCheck, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useT } from "@/i18n";
import { PageContainer, PageHeader } from "@pervasivemind/admin-ui";
import {
  sendPolicyApi,
  type SendAuditList,
  type SendAuditRow,
  type SendPolicy,
} from "@/lib/builder-api";
import {
  integrationsApi,
  type ChannelCredential,
} from "@/lib/integrations-api";

const inputCls = "w-full rounded border border-neutral-300 px-2 py-1";

/** Rows per audit page. The endpoint caps ``limit`` at 200. */
const AUDIT_PAGE_SIZE = 50;

/**
 * Outcomes the send pipeline records today.
 *
 * A value outside this list renders RAW, in monospace, with a tooltip
 * saying the label is missing — it is never prettified into something
 * plausible. An invented label on an audit of customer data leaving the
 * platform is worse than an ugly one.
 */
export const KNOWN_OUTCOMES = [
  "queued",
  "collapsed",
  "schedule_queued",
  "capture_token",
  "prompt_blocked",
  // Result rows the builder writes since 2026-09-10:
  // service_delivery.py USER_OUTCOMES + SCHEDULE_REFUSALS.
  "sent",
  "failed",
  "dead",
  "reaped",
  "schedule_skipped",
  "schedule_failed",
] as const;
export type KnownOutcome = (typeof KNOWN_OUTCOMES)[number];

export function isKnownOutcome(v: string): v is KnownOutcome {
  return (KNOWN_OUTCOMES as readonly string[]).includes(v);
}

/** Every known outcome names its colour — the Record has no default arm. */
export const OUTCOME_VARIANT: Record<
  KnownOutcome,
  "success" | "warning" | "error"
> = {
  queued: "success",
  collapsed: "success",
  schedule_queued: "success",
  capture_token: "success",
  prompt_blocked: "warning",
  sent: "success",
  reaped: "warning",
  schedule_skipped: "warning",
  failed: "error",
  dead: "error",
  schedule_failed: "error",
};

/** What the first load got back — each half on its own (#257). */
export interface SendPolicyLoad {
  policy: SendPolicy | null;
  policyError: string | null;
  audit: SendAuditList | null;
  auditError: string | null;
}

/**
 * A failed audit must not throw away a policy that loaded, and a
 * failed policy must leave nothing the form could save — its Save
 * PUTs every field, so an unloaded form overwrites the real policy.
 */
export async function loadSendPolicyScreen(): Promise<SendPolicyLoad> {
  const [pol, aud] = await Promise.allSettled([
    sendPolicyApi.get(),
    sendPolicyApi.audit(AUDIT_PAGE_SIZE, 0),
  ]);
  const message = (reason: unknown): string =>
    reason instanceof Error ? reason.message : String(reason);
  return {
    policy: pol.status === "fulfilled" ? pol.value : null,
    policyError: pol.status === "rejected" ? message(pol.reason) : null,
    audit: aud.status === "fulfilled" ? aud.value : null,
    auditError: aud.status === "rejected" ? message(aud.reason) : null,
  };
}

export function SendPolicyPage(): JSX.Element {
  const { t, locale } = useT();
  const [policy, setPolicy] = useState<SendPolicy | null>(null);
  const [credentials, setCredentials] = useState<ChannelCredential[]>([]);
  const [audit, setAudit] = useState<SendAuditRow[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  // The audit listed the first 50 of thousands with no way to reach the
  // rest: the endpoint always took an offset, the screen just never sent
  // one. This is a log of customer data leaving the platform, so "you can
  // see the newest 50" is not a reasonable place to stop.
  const [auditPage, setAuditPage] = useState(1);
  const [auditLoading, setAuditLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Why the form is missing — apart from ``error``, which the ✕ and a
  // page change both clear (#257).
  const [policyError, setPolicyError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  // Form state (verbatim mirror of SendPolicyInput).
  const [enabled, setEnabled] = useState(false);
  const [domains, setDomains] = useState<string[]>([]);
  const [domainDraft, setDomainDraft] = useState("");
  const [maxRecipients, setMaxRecipients] = useState(5);
  const [quota, setQuota] = useState<number | null>(null);
  const [credentialId, setCredentialId] = useState("");

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const out = await loadSendPolicyScreen();
      if (out.policy !== null) {
        setPolicy(out.policy);
        setEnabled(out.policy.enabled);
        setDomains(out.policy.allowed_domains);
        setMaxRecipients(out.policy.max_recipients);
        setQuota(out.policy.daily_quota_per_user);
        setCredentialId(out.policy.email_credential_id ?? "");
      }
      setPolicyError(out.policyError);
      if (out.audit !== null) {
        setAudit(out.audit.rows);
        setAuditTotal(out.audit.total);
        setAuditPage(1);
      }
      setError(out.auditError);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const goToAuditPage = useCallback(
    async (next: number): Promise<void> => {
      setAuditLoading(true);
      try {
        const out = await sendPolicyApi.audit(
          AUDIT_PAGE_SIZE,
          (next - 1) * AUDIT_PAGE_SIZE,
        );
        setAudit(out.rows);
        setAuditTotal(out.total);
        setAuditPage(next);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "error");
      } finally {
        setAuditLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    integrationsApi.channels
      .list(locale)
      .then((out) =>
        setCredentials(out.credentials.filter((c) => c.channel === "email")),
      )
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : String(err)),
      );
  }, [locale]);

  const addDomain = (): void => {
    const d = domainDraft.trim().toLowerCase().replace(/^@/, "");
    setDomainDraft("");
    if (!d || domains.includes(d)) return;
    setDomains((ds) => [...ds, d]);
  };

  const enableBlocked = useMemo(() => {
    if (!enabled) return null;
    if (domains.length === 0) return t("sendPolicy.needDomain");
    if (!credentialId) return t("sendPolicy.needCredential");
    return null;
  }, [enabled, domains, credentialId, t]);

  const handleSave = async (): Promise<void> => {
    if (policy === null) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await sendPolicyApi.put({
        enabled,
        allowed_domains: domains,
        max_recipients: maxRecipients,
        daily_quota_per_user: quota,
        email_credential_id: credentialId || null,
      });
      setPolicy(saved);
      setFeedback(t("common.saved"));
      setTimeout(() => setFeedback(null), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-10 text-sm text-neutral-500">
        <Spinner size={16} /> {t("common.loading")}
      </div>
    );
  }

  return (
    // The page container every other operator page uses: this one sat
    // flush against the shell with no gutter.
    <PageContainer className="space-y-4">
      <PageHeader
        title={t("sendPolicy.title")}
        subtitle={t("sendPolicy.subtitle")}
      />

      {feedback && (
        <div className="flex items-center gap-2 rounded-lg border border-success/40 bg-success/10 px-4 py-2 text-sm text-success-dark">
          <Check size={14} /> {feedback}
        </div>
      )}
      {error && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-error/40 bg-error/5 px-4 py-2 text-sm text-error-dark"
        >
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span className="whitespace-pre-wrap">{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-auto text-error-dark"
            aria-label={t("common.cancel")}
          >
            ✕
          </button>
        </div>
      )}

      {policy === null ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-error/40 bg-error/5 px-4 py-2 text-sm text-error-dark"
        >
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span className="whitespace-pre-wrap">
            {t("sendPolicy.loadFailed")}
            {"\n"}
            {policyError}
          </span>
        </div>
      ) : (
        <Card>
          <CardBody className="space-y-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={enabled}
                aria-label={t("sendPolicy.enabled")}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              <span className="font-medium text-neutral-800">
                {t("sendPolicy.enabled")}
              </span>
              {policy?.configured === false && (
                <Badge variant="neutral" size="sm">
                  {t("sendPolicy.neverConfigured")}
                </Badge>
              )}
            </label>
            {enableBlocked && (
              <p className="flex items-center gap-2 rounded border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-neutral-800">
                <ShieldCheck size={14} /> {enableBlocked}
              </p>
            )}

            <label className="block text-sm">
              <span className="mb-1 block text-neutral-600">
                {t("sendPolicy.domains")}
              </span>
              <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-neutral-300 px-2 py-1.5">
                {domains.map((d) => (
                  <span
                    key={d}
                    className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5 text-xs text-accent"
                  >
                    {d}
                    <button
                      type="button"
                      aria-label={t("sendPolicy.removeDomain", { domain: d })}
                      onClick={() =>
                        setDomains((ds) => ds.filter((x) => x !== d))
                      }
                    >
                      <X size={11} />
                    </button>
                  </span>
                ))}
                <input
                  className="min-w-[10rem] flex-1 border-0 py-0.5 text-sm outline-none"
                  value={domainDraft}
                  placeholder="cliente.com"
                  aria-label={t("sendPolicy.domains")}
                  onChange={(e) => setDomainDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "," || e.key === " ") {
                      e.preventDefault();
                      addDomain();
                    }
                  }}
                  onBlur={addDomain}
                />
              </div>
              <span className="mt-1 block text-xs text-neutral-500">
                {t("sendPolicy.domainsHint")}
              </span>
            </label>

            <div className="grid gap-3 sm:grid-cols-3">
              <label className="text-sm">
                <span className="mb-1 block text-neutral-600">
                  {t("sendPolicy.maxRecipients")}
                </span>
                <input
                  type="number"
                  min={1}
                  max={50}
                  className={inputCls}
                  value={maxRecipients}
                  aria-label={t("sendPolicy.maxRecipients")}
                  onChange={(e) => setMaxRecipients(Number(e.target.value))}
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-neutral-600">
                  {t("sendPolicy.quota")}
                </span>
                <input
                  type="number"
                  min={1}
                  max={1000}
                  className={inputCls}
                  value={quota ?? ""}
                  placeholder={t("sendPolicy.quotaNone")}
                  aria-label={t("sendPolicy.quota")}
                  onChange={(e) =>
                    setQuota(
                      e.target.value === "" ? null : Number(e.target.value),
                    )
                  }
                />
                <span className="mt-1 block text-xs text-neutral-500">
                  {t("sendPolicy.quotaHint")}
                </span>
              </label>
              <Select
                label={t("sendPolicy.credential")}
                options={[
                  { value: "", label: t("sendPolicy.credentialNone") },
                  ...credentials.map((c) => ({
                    value: c.id,
                    // A credential with no secret configured keeps saying so
                    // in its own label — it is selectable, and picking one
                    // is how a send fails later with missing_secret.
                    label: c.secret.configured
                      ? c.name
                      : `${c.name} — ${t("integrations.secret.notConfigured")}`,
                  })),
                ]}
                value={credentialId}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setCredentialId(e.target.value)
                }
                fullWidth
              />
            </div>

            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={saving}
                onClick={() => void handleSave()}
              >
                {saving ? <Spinner size={14} /> : t("common.save")}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      <div>
        <h2 className="text-base font-semibold text-neutral-900">
          {t("sendPolicy.audit.title")}
          <span className="ml-2 text-sm font-normal text-neutral-500">
            {t("sendPolicy.audit.total", { n: auditTotal })}
          </span>
        </h2>
        {audit.length === 0 ? (
          <p className="py-6 text-sm text-neutral-500">
            {t("sendPolicy.audit.empty")}
          </p>
        ) : (
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-200 text-left text-xs text-neutral-500">
                  <th className="py-2 pr-2">{t("sendPolicy.audit.when")}</th>
                  <th className="py-2 pr-2">{t("sendPolicy.audit.who")}</th>
                  <th className="py-2 pr-2">{t("sendPolicy.audit.report")}</th>
                  <th className="py-2 pr-2">
                    {t("sendPolicy.audit.recipients")}
                  </th>
                  <th className="py-2 pr-2">{t("sendPolicy.audit.extras")}</th>
                  <th className="py-2 pr-2">{t("sendPolicy.audit.outcome")}</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((row) => (
                  <tr key={row.id} className="border-b border-neutral-100">
                    <td className="py-2 pr-2 text-neutral-600">
                      {new Date(
                        row.sent_at.endsWith("Z")
                          ? row.sent_at
                          : `${row.sent_at}Z`,
                      ).toLocaleString()}
                    </td>
                    <td className="py-2 pr-2 text-neutral-800">
                      {row.user_email}
                    </td>
                    <td className="py-2 pr-2 text-neutral-600">
                      {row.report_key}
                    </td>
                    <td className="py-2 pr-2 text-neutral-600">
                      {row.recipients.join(", ")}
                    </td>
                    <td className="py-2 pr-2 text-xs text-neutral-500">
                      {[
                        row.had_screenshot
                          ? t("sendPolicy.audit.image")
                          : null,
                        row.had_ai ? "AI" : null,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "—"}
                    </td>
                    <td className="py-2 pr-2">
                      {isKnownOutcome(row.outcome) ? (
                        <Badge
                          variant={OUTCOME_VARIANT[row.outcome]}
                          size="sm"
                        >
                          {t(`sendPolicy.outcome.${row.outcome}`)}
                        </Badge>
                      ) : (
                        <Badge
                          variant="warning"
                          size="sm"
                          title={t("sendPolicy.outcome.unknown")}
                        >
                          <span className="font-mono">{row.outcome}</span>
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {auditTotal > AUDIT_PAGE_SIZE && (
          <div className="mt-3 flex items-center justify-between gap-3">
            <span className="text-[13px] text-neutral-500">
              {t("sendPolicy.audit.pageOf", {
                page: auditPage,
                pages: Math.ceil(auditTotal / AUDIT_PAGE_SIZE),
              })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                ghost
                size="sm"
                disabled={auditPage <= 1 || auditLoading}
                onClick={() => void goToAuditPage(auditPage - 1)}
              >
                {t("sendPolicy.audit.prev")}
              </Button>
              <Button
                ghost
                size="sm"
                disabled={
                  auditPage >= Math.ceil(auditTotal / AUDIT_PAGE_SIZE) ||
                  auditLoading
                }
                onClick={() => void goToAuditPage(auditPage + 1)}
              >
                {t("sendPolicy.audit.next")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </PageContainer>
  );
}

export default SendPolicyPage;
